import type {
  SphereEffect,
  SpherePlayerView,
  SpherePotionId,
  SphereSpellId,
} from './sim/sphereDuel';
import { SPHERE_POTION_COOLDOWN_TICKS, SPHERE_SPELLS } from './sim/sphereDuel';
import { SPHERE_HOTBAR } from './sim/sphereIcons';
import { sphereIconUrl } from './sphereIconAssets';
import { extrapolateSphereTicks, SPHERE_TICK_MS } from './sphereClock';
import {
  SPHERE_SLOT_ACTIONS,
  defaultBindings,
  formatBinding,
  type BindingTable,
} from './input/keybindings';

export type SphereHotbarAction =
  | { kind: 'cast'; spell: SphereSpellId }
  | { kind: 'potion'; potion: SpherePotionId }
  | { kind: 'cancel' }
  | { kind: 'rematch' }
  | { kind: 'exit' };

export interface SphereHudState extends SpherePlayerView {
  opponentName: string;
  record: { wins: number; losses: number; draws: number };
  wager: number;
  reconnectTicks?: number;
  result?: 'win' | 'loss' | 'draw' | 'cancelled';
  rematch?: { you: boolean; opponent: boolean };
}

export interface SphereHud {
  render(state: SphereHudState): void;
  effect(effect: SphereEffect): void;
  acknowledge?(text: string): void;
  hide(): void;
  /**
   * Fire hotbar slot `slot` (0-based) as if its button were clicked, gated the same
   * way: a no-op while the hotbar is hidden or the slot is unavailable. The sphere
   * installer's `sphere-slot-N` action handlers press through this, so the hotkey
   * dispatch in `main.ts` stays free of hotbar state.
   */
  pressSlot?(slot: number): void;
  /** Settle hotbar icon loads; absence is an explicit browser fallback. */
  ready?(): Promise<void>;
}

export function initSphereHud(
  onAction: (action: SphereHotbarAction) => void,
  doc: Document = document,
  // The live binding table (rebinds in Settings apply without a reload); the
  // defaults keep tests and any bindings-less caller on the stock Digit1-8 badges.
  getBindings: () => BindingTable = defaultBindings,
): SphereHud {
  if (!doc.body) {
    return { render: () => {}, effect: () => {}, hide: () => {}, ready: () => Promise.resolve() };
  }
  const root = doc.createElement('section');
  root.id = 'sphere-hud';
  root.hidden = true;
  root.setAttribute('aria-label', 'Sphere Duel');
  const hotbar = doc.createElement('div');
  hotbar.className = 'sphere-hotbar sphere-hotbar-gump';
  hotbar.setAttribute('aria-label', 'Sphere Duel spell hotbar');
  const matchCard = doc.createElement('aside');
  matchCard.className = 'sphere-match-card';
  matchCard.setAttribute('aria-label', 'Sphere Duel match information');
  const heading = doc.createElement('div');
  heading.className = 'sphere-match-heading';
  const title = doc.createElement('span');
  title.textContent = 'Sphere Duel';
  const clock = doc.createElement('span');
  clock.className = 'sphere-match-clock';
  heading.append(title, clock);
  const opponent = doc.createElement('strong');
  opponent.className = 'sphere-match-opponent';
  const status = doc.createElement('div');
  status.id = 'sphere-status';
  const record = doc.createElement('div');
  record.className = 'sphere-match-record';
  matchCard.append(heading, opponent, record, status);
  const announcement = doc.createElement('div');
  announcement.id = 'sphere-announcement';
  announcement.hidden = true;
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', 'assertive');
  announcement.setAttribute('aria-atomic', 'true');
  const resultOverlay = doc.createElement('div');
  resultOverlay.id = 'sphere-result-overlay';
  resultOverlay.hidden = true;
  resultOverlay.setAttribute('role', 'dialog');
  resultOverlay.setAttribute('aria-modal', 'true');
  resultOverlay.setAttribute('aria-labelledby', 'sphere-result-title');
  const resultCard = doc.createElement('div');
  resultCard.className = 'sphere-result-card';
  const resultTitle = doc.createElement('strong');
  resultTitle.id = 'sphere-result-title';
  const resultWager = doc.createElement('span');
  resultWager.className = 'sphere-result-settlement';
  const resultStatus = doc.createElement('span');
  resultStatus.className = 'sphere-result-status';
  const resultActions = doc.createElement('div');
  resultActions.className = 'sphere-result-actions';
  const rematchButton = doc.createElement('button');
  rematchButton.type = 'button';
  rematchButton.dataset.sphereResultAction = 'rematch';
  rematchButton.textContent = 'Rematch';
  const exitButton = doc.createElement('button');
  exitButton.type = 'button';
  exitButton.dataset.sphereResultAction = 'exit';
  exitButton.textContent = 'Exit duel';
  resultActions.append(rematchButton, exitButton);
  resultCard.append(resultTitle, resultWager, resultStatus, resultActions);
  resultOverlay.append(resultCard);
  rematchButton.addEventListener('click', () => {
    rematchButton.disabled = true;
    resultStatus.textContent = `Waiting for ${opponent.textContent || 'opponent'}...`;
    onAction({ kind: 'rematch' });
  });
  exitButton.addEventListener('click', () => onAction({ kind: 'exit' }));
  const buttons: HTMLButtonElement[] = [];
  const keyBadges: HTMLSpanElement[] = [];
  const cooldownIndicators: HTMLSpanElement[] = [];
  const iconLoads: Promise<void>[] = [];
  SPHERE_HOTBAR.forEach((entry) => {
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'sphere-hotbar-slot';
    button.dataset.kind = entry.action.kind;
    if (entry.action.kind === 'potion') button.classList.add('sphere-hotbar-slot--potion');
    const img = doc.createElement('img');
    img.alt = '';
    const iconUrl = sphereIconUrl(entry);
    if (iconUrl) {
      img.src = iconUrl;
      const decode = img.decode?.bind(img);
      iconLoads.push(decode ? decode().catch(() => {}) : Promise.resolve());
    } else {
      iconLoads.push(Promise.resolve());
    }
    const key = doc.createElement('span');
    key.className = 'sphere-hotbar-key';
    keyBadges.push(key);
    // Name and cost live in a hover/focus tooltip; only the key badge stays on
    // the slot face so the icon art fills the box.
    const tip = doc.createElement('span');
    tip.className = 'sphere-hotbar-tip';
    const tipName = doc.createElement('strong');
    tipName.className = 'sphere-hotbar-tip-name';
    tipName.textContent = entry.label;
    const tipCost = doc.createElement('span');
    tipCost.className = 'sphere-hotbar-tip-cost';
    tipCost.textContent = entry.action.kind === 'cast'
      ? `${SPHERE_SPELLS[entry.action.spell].mana} mana`
      : 'shared CD';
    tip.append(tipName, tipCost);
    button.append(img, key);
    if (entry.action.kind === 'potion') {
      const cooldown = doc.createElement('span');
      cooldown.className = 'sphere-hotbar-cooldown';
      cooldown.hidden = true;
      cooldown.setAttribute('aria-hidden', 'true');
      cooldownIndicators.push(cooldown);
      button.append(cooldown);
    }
    button.append(tip);
    button.addEventListener('click', () => onAction(entry.action));
    hotbar.append(button);
    buttons.push(button);
  });
  // The slots' key badges + tooltips reflect the *bound* keys, not a hardcoded 1-8
  // (settings-overhaul: the slots are rebindable registry actions). Re-run on every
  // render so a mid-duel rebind shows up on the next state tick.
  const refreshHotkeys = (): void => {
    SPHERE_HOTBAR.forEach((entry, index) => {
      const action = SPHERE_SLOT_ACTIONS[index];
      const binding = action ? getBindings()[action] : undefined;
      const keyLabel = binding ? formatBinding(binding) : String(index + 1);
      const badge = keyBadges[index];
      if (badge && badge.textContent !== keyLabel) badge.textContent = keyLabel;
      // aria-label only - a native title would double up with the styled tooltip.
      const description = `${keyLabel}. ${entry.label}`;
      const button = buttons[index];
      if (button && button.getAttribute('aria-label') !== description) {
        button.setAttribute('aria-label', description);
      }
    });
  };
  refreshHotkeys();
  root.append(hotbar, matchCard, announcement);
  doc.body.append(root, resultOverlay);

  let transientStatus: { text: string; expiresAt: number } | undefined;
  let transientAnnouncement: { text: string; expiresAt: number } | undefined;
  let shownAnnouncement: string | undefined;
  let latestState: SphereHudState | undefined;
  let stateReceivedAt = 0;
  let timer: number | undefined;

  const setText = (element: HTMLElement, text: string): void => {
    if (element.textContent !== text) element.textContent = text;
  };
  const setHidden = (element: HTMLElement, hidden: boolean): void => {
    if (element.hidden !== hidden) element.hidden = hidden;
  };
  const setDisabled = (element: HTMLButtonElement, disabled: boolean): void => {
    if (element.disabled !== disabled) element.disabled = disabled;
  };

  const renderAnnouncement = (text?: string): void => {
    if (!text) {
      setHidden(announcement, true);
      announcement.classList.remove('sphere-announcement-pop');
      shownAnnouncement = undefined;
      return;
    }
    setHidden(announcement, false);
    setText(announcement, text);
    const kind = text === 'FIGHT!' ? 'fight' : 'countdown';
    if (announcement.dataset.kind !== kind) announcement.dataset.kind = kind;
    if (shownAnnouncement !== text) {
      announcement.classList.remove('sphere-announcement-pop');
      void announcement.offsetWidth;
      announcement.classList.add('sphere-announcement-pop');
      shownAnnouncement = text;
    }
  };

  const isTextEntry = (target: EventTarget | null): boolean => {
    const ElementType = doc.defaultView?.Element;
    if (!ElementType || !(target instanceof ElementType)) return false;
    const element = target as Element;
    return element.matches('input, textarea, select') ||
      element.closest('[contenteditable]:not([contenteditable="false"])') !== null;
  };

  // Escape cancels an in-progress cast. The slot hotkeys are NOT handled here:
  // they are registry actions (sphere-slot-1..8) dispatched through main.ts's
  // single hotkey path into `pressSlot`, so they rebind in Settings like any other
  // hotkey. Escape stays local - it is a reserved, unbindable key.
  doc.defaultView?.addEventListener('keydown', (event) => {
    if (root.hidden || event.repeat || isTextEntry(event.target)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onAction({ kind: 'cancel' });
    }
  });

  const updateDynamic = (): void => {
    const state = latestState;
    if (!state?.active) return;
    const now = Date.now();
    const remaining = extrapolateSphereTicks(
      { phase: state.phase, remainingTicks: state.remainingTicks, receivedAtMs: stateReceivedAt },
      now,
    );
    const seconds = Math.ceil(remaining / 20);
    setText(clock, `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`);
    const elapsedTicks = state.phase === 'fighting'
      ? Math.max(0, Math.floor((now - stateReceivedAt) / SPHERE_TICK_MS))
      : 0;
    const cooldown = Math.max(0, state.potionCooldownTicks - elapsedTicks);
    const cooldownSeconds = Math.ceil(cooldown / 20);
    const cooldownAngle =
      `${Math.max(0, 1 - cooldown / SPHERE_POTION_COOLDOWN_TICKS) * 360}deg`;
    cooldownIndicators.forEach((indicator) => {
      setHidden(indicator, cooldown <= 0);
      setText(indicator, cooldown > 0 ? String(cooldownSeconds) : '');
      if (indicator.style.getPropertyValue('--sphere-cooldown-angle') !== cooldownAngle) {
        indicator.style.setProperty('--sphere-cooldown-angle', cooldownAngle);
      }
    });
    const visibleTransient = transientStatus && transientStatus.expiresAt > now
      ? transientStatus.text
      : undefined;
    if (!visibleTransient) transientStatus = undefined;
    const visibleAnnouncement =
      transientAnnouncement && transientAnnouncement.expiresAt > now
        ? transientAnnouncement.text
        : undefined;
    if (!visibleAnnouncement) transientAnnouncement = undefined;
    if (state.phase === 'paused') {
      setText(status, `Waiting for reconnect - ${Math.ceil((state.reconnectTicks ?? 0) / 20)}s`);
    } else if (state.phase === 'result' && state.result) {
      setText(status, resultTitle.textContent ?? '');
    } else if (visibleTransient) {
      setText(status, visibleTransient);
    } else if (state.suddenDeath) {
      setText(status, 'SUDDEN DEATH - HEALING DISABLED');
    } else if (state.phase !== 'fighting') {
      setText(status, state.phase.toUpperCase());
    } else {
      setText(
        status,
        cooldown > 0 ? `Potion cooldown ${cooldownSeconds}s` : 'Duel in progress',
      );
    }
    renderAnnouncement(
      state.phase === 'countdown'
        ? String(Math.max(1, Math.ceil(remaining / 20)))
        : visibleAnnouncement,
    );
    if (state.phase === 'result' && state.result && !state.rematch?.you && !state.rematch?.opponent) {
      setText(resultStatus, `Return to the bank in ${seconds}s`);
    }
  };

  const startTimer = (): void => {
    if (timer !== undefined || !doc.defaultView) return;
    timer = doc.defaultView.setInterval(updateDynamic, SPHERE_TICK_MS);
  };
  const stopTimer = (): void => {
    if (timer === undefined || !doc.defaultView) return;
    doc.defaultView.clearInterval(timer);
    timer = undefined;
  };

  return {
    render(state): void {
      latestState = state;
      stateReceivedAt = Date.now();
      setHidden(root, !state.active);
      if (!state.active) {
        setHidden(resultOverlay, true);
        stopTimer();
        return;
      }
      startTimer();
      refreshHotkeys();
      setText(opponent, state.opponentName);
      setText(record, `Record ${state.record.wins}-${state.record.losses}-${state.record.draws}${state.wager > 0 ? `  |  ${state.wager} gp stake` : ''}`);
      setHidden(resultOverlay, state.phase !== 'result' || state.result === undefined);
      if (!resultOverlay.hidden) {
        setText(resultTitle, state.result === 'win'
          ? 'VICTORY'
          : state.result === 'loss'
            ? 'DEFEAT'
            : state.result === 'draw'
              ? 'DRAW'
              : 'MATCH CANCELLED');
        setText(resultWager, state.wager === 0
          ? 'Honor duel - no gold changed hands'
          : state.result === 'win'
            ? `${state.wager * 2} gp pot won`
            : state.result === 'loss'
              ? `${state.wager * 2} gp pot awarded to ${state.opponentName}`
              : `${state.wager} gp stake refunded`);
        setHidden(rematchButton, state.rematch === undefined);
        setDisabled(rematchButton, state.rematch?.you ?? false);
        setText(resultStatus, state.rematch?.you
          ? state.rematch.opponent
            ? 'Starting rematch...'
            : `Waiting for ${state.opponentName}...`
          : state.rematch?.opponent
            ? `${state.opponentName} wants a rematch`
            : '');
      }
      updateDynamic();
      SPHERE_HOTBAR.forEach((entry, index) => {
        const key = entry.action.kind === 'cast'
          ? entry.action.spell
          : entry.action.kind === 'potion'
            ? (`potion-${entry.action.potion}` as const)
            : undefined;
        setDisabled(buttons[index]!, key === undefined || !state.available[key]);
      });
    },
    effect(effect): void {
      const text = effect.kind === 'announcement'
        ? effect.text
        : effect.kind === 'cast-start'
          ? effect.words
          : effect.kind === 'fizzle'
            ? '*fizzle*'
            : undefined;
      if (text) {
        if (effect.kind === 'announcement' && /^(?:[123]|FIGHT!)$/.test(text)) {
          transientAnnouncement = { text, expiresAt: Date.now() + 1_000 };
          renderAnnouncement(text);
        } else {
          transientStatus = { text, expiresAt: Date.now() + 1_000 };
          setText(status, text);
        }
      }
    },
    acknowledge(text): void {
      transientStatus = { text, expiresAt: Date.now() + 1_000 };
      setText(status, text);
    },
    pressSlot(slot): void {
      if (root.hidden) return;
      const entry = SPHERE_HOTBAR[slot];
      if (!entry || buttons[slot]?.disabled !== false) return;
      onAction(entry.action);
    },
    hide(): void {
      latestState = undefined;
      setHidden(root, true);
      setHidden(resultOverlay, true);
      stopTimer();
    },
    ready(): Promise<void> {
      return Promise.all(iconLoads).then(() => undefined);
    },
  };
}
